import React, { Fragment } from "react";
import InvertableStack from "../../../components/wrappers/InvertableStack";
import TimeSlots from "./TimeSlots";
import Date from "../../../components/form/Date";
import * as WORKING_HOURS from "../utils/WorkingHoursRepositroy";
import dayjs from "dayjs";
import { useFetch } from "../../../hooks/Fetching";
import SimpleAlert from "../../../components/display/SimpleAlert";
import useUser from "../../../hooks/useUser";
import { useForm, useFormContext } from "react-hook-form";
import { mandatory } from "../../../utils/Functions";
import { useTranslation } from "react-i18next";
import { AppointmentType } from "../../../components/modals/bookingModalComponents/context/BookingModalContext";
import useGetAvailableTimeSlots from "../../../hooks/useGetAvailableTimeSlots";

export default function AppointmentTimePicker({ warningMessage, testId }) {
  const { t, i18n } = useTranslation();
  const {
    control,
    setValue,
    clearErrors,
    register,
    getValues,
    formState: { errors, touchedFields },
    watch,
  } = useFormContext();
  const { roles, currentClinic } = useUser();
  const [endTimeAvailableHours, setEndTimeAvailableHours] = React.useState([]);
  const [userSelectedStartTime, setUserSelectedStartTime] = React.useState();
  const workingDaysDto = useFetch(
    "/clinics/working_days/dto/",
    currentClinic?.id,
    "working_days_dto",
    true,
    0,
  );
  const workingDays = workingDaysDto?.data?.workingDays;
  const workingDaysSet = workingDaysDto?.data?.workingDaySet;

  const selectedDate = watch("startDate");
  const selectedService = watch(AppointmentType);
  const serviceId = selectedService?.id;
  const selectedStartTime = watch("time");

  const {
    data: slotData, //{ availableHours: [], booked: [] }
    isLoading: isSlotsLoading,
    error: slotsError,
  } = useGetAvailableTimeSlots(serviceId, selectedDate);

  const availableHours = slotData?.availableHours || [];
  const booked = slotData?.booked || [];

  React.useEffect(() => {
    setUserSelectedStartTime(getValues("time"));
  });

  // Clears times when date OR service changes
  React.useEffect(() => {
    const subscription = watch((value, { name }) => {
      // Only clear if it's NOT the initial mount
      const isUserInteraction = touchedFields[name] || touchedFields.startDate;
      if (
        (name === AppointmentType || name === "startDate") &&
        isUserInteraction
      ) {
        setValue("time", null);
        setValue("endTime", null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, touchedFields]);

  // This effect calculates end times whenever the start time or date changes.
  React.useEffect(() => {
    //Calculate end times.
    if (selectedDate && selectedStartTime && roles.adminOrEmployee) {
      setUserSelectedStartTime(selectedStartTime);
      setEndTimeAvailableHours(
        WORKING_HOURS.getTimeAfter(selectedStartTime, availableHours),
      );
    } else {
      // If prerequisites are not met, clear the end time slots
      setEndTimeAvailableHours([]);
    }
  }, [selectedDate, selectedStartTime, roles.adminOrEmployee, availableHours]);

  const onDateChangeInternal = (newDate) => {
    setValue("startDate", newDate);
  };

  const handleStartTimeChange = (time) => {
    setUserSelectedStartTime(time);
    setEndTimeAvailableHours(WORKING_HOURS.getTimeAfter(time, availableHours));
    const startTime = dayjs(time, "HH:mm");
    const endTime = dayjs(getValues("endTime"), "HH:mm");
    if (endTime && (startTime.isAfter(endTime) || startTime.isSame(endTime))) {
      setValue("endTime", null);
    }
  };

  if (workingDaysDto.error) {
    return (
      <SimpleAlert message={"ErrorLoadingWorkingDays"} severity={"error"} />
    );
  }

  return (
    <InvertableStack>
      <Date
        disabled={workingDaysDto.isLoading}
        name={"startDate"}
        control={control}
        errors={errors}
        disablePast
        onAccept={onDateChangeInternal}
        shouldDisableDate={(date) =>
          WORKING_HOURS.isWorkingDay(
            date,
            workingDaysSet,
            roles.adminOrEmployee,
          )
        }
        key={"booking-date-picker"}
        testId={testId && `${testId}.date-input`}
      />

      {roles.adminOrEmployee ? (
        <Fragment>
          <TimeSlots
            placeholder={mandatory(t("StartTime"))}
            workingHours={workingDays}
            clearErrors={clearErrors}
            errors={errors}
            register={register}
            setValue={setValue}
            warningMessage={warningMessage}
            userSelectedDate={selectedDate}
            availableHours={availableHours}
            handleStartTimeChange={handleStartTimeChange}
            tag="time"
            key={"booking-time-slots"}
            selectedService={selectedService}
            isLoading={isSlotsLoading}
            fetchError={slotsError}
            testId={testId && `${testId}.time-input`}
          />
          <TimeSlots
            placeholder="EndTime"
            workingHours={workingDays}
            clearErrors={clearErrors}
            errors={errors}
            register={register}
            setValue={setValue}
            userSelectedDate={selectedDate}
            availableHours={endTimeAvailableHours}
            userSelectedStartTime={userSelectedStartTime}
            tag="endTime"
            key={"booking-endTime-slots"}
            selectedService={selectedService}
            isLoading={isSlotsLoading}
            fetchError={slotsError}
            testId={testId && `${testId}.endtime-input`}
          />
        </Fragment>
      ) : (
        <TimeSlots
          placeholder={mandatory(t("StartTime"))}
          workingHours={workingDays}
          clearErrors={clearErrors}
          errors={errors}
          register={register}
          setValue={setValue}
          warningMessage={warningMessage}
          userSelectedDate={selectedDate}
          availableHours={availableHours}
          bookedHours={booked}
          handleStartTimeChange={handleStartTimeChange}
          tag="time"
          key={"booking-time-slots"}
          selectedService={selectedService}
          isLoading={isSlotsLoading}
          fetchError={slotsError}
          testId={testId && `${testId}.time-input`}
        />
      )}
    </InvertableStack>
  );
}
